import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, MapPin, ArrowRight } from "lucide-react";
import { LOGO_DARK_BG, CONTACT } from "@/lib/brand";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const LOCATIONS = [
    { city: "Mumbai, India", phone: CONTACT.display },
];

const QUICK_LINKS = [
    { l: "Buy",          to: "/properties?listing_type=sale" },
    { l: "Rent",         to: "/properties?listing_type=rent" },
    { l: "Auction Properties", to: "/auction-properties" },
    { l: "Land Properties", to: "/land" },
    { l: "Dealer Properties", to: "/dealer-properties" },
    { l: "Media Coverage", to: "/about/media-coverage" },
    { l: "All Brands",   to: "/brands" },
    { l: "Developments", to: "/properties" },
    { l: "Investments",  to: "/services" },
    { l: "Services",     to: "/services" },
    { l: "About Us",     to: "/about" },
    { l: "Contact",      to: "/contact" },
];

const IMPORTANT_LINKS = [
    { l: "Legal Check",         to: "/legal-check" },
    { l: "Insights",            to: "/insights" },
    { l: "Careers",             to: "/careers" },
    { l: "Newsroom",            to: "/insights" },
    { l: "Sitemap",             to: "/sitemap" },
];

const POLICY_LINKS = [
    { l: "Privacy Policy",     to: "/privacy-policy" },
    { l: "Terms & Conditions", to: "/terms-conditions" },
    { l: "Cookie Policy",      to: "/cookie-policy" },
    { l: "RERA Disclaimer",    to: "/rera-disclaimer" },
];

export default function Footer() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setBusy(true);
        try {
            await api.post("/newsletter", { email });
            toast.success("Subscribed to EBG Insights.");
            setEmail("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <footer data-testid="site-footer" className="bg-[#0a0d18] text-[var(--ebg-ivory)]">
            {/* Main grid */}
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-16 pb-10 grid grid-cols-2 md:grid-cols-12 gap-10">
                {/* Brand column */}
                <div className="col-span-2 md:col-span-3">
                    <div className="flex flex-col items-start">
                        <img src={LOGO_DARK_BG} alt="EBG Realty" className="h-14 w-auto" />
                    </div>
                    <p className="text-white/60 text-sm mt-6 leading-relaxed max-w-[280px]">
                        EBG Realty is India&rsquo;s premier luxury real estate advisory firm offering bespoke
                        solutions for discerning clients and global investors.
                    </p>
                    <div className="flex gap-3 mt-6">
                        {[
                            { Icon: Facebook,  testid: "social-facebook",  href: "#" },
                            { Icon: Instagram, testid: "social-instagram", href: "#" },
                            { Icon: Linkedin,  testid: "social-linkedin",  href: "#" },
                            { Icon: Youtube,   testid: "social-youtube",   href: "#" },
                        ].map(({ Icon, testid, href }) => (
                            <a
                                key={testid}
                                href={href}
                                data-testid={testid}
                                className="w-9 h-9 rounded-full border border-[var(--ebg-gold-line)] flex items-center justify-center text-[var(--ebg-gold)] hover:bg-[var(--ebg-gold)] hover:text-black transition-colors"
                                aria-label={testid}
                            >
                                <Icon size={14} />
                            </a>
                        ))}
                    </div>
                </div>

                {/* Quick links */}
                <div className="col-span-1 md:col-span-2">
                    <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)] mb-5">Quick Links</div>
                    <ul className="space-y-3">
                        {QUICK_LINKS.map((l) => (
                            <li key={l.l}>
                                <Link
                                    to={l.to}
                                    data-testid={`footer-quick-${l.l.toLowerCase().replace(/\s+/g,'-')}`}
                                    className="text-sm text-white/75 hover:text-[var(--ebg-gold)] transition-colors"
                                >
                                    {l.l}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Important links */}
                <div className="col-span-1 md:col-span-2">
                    <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)] mb-5">Important Links</div>
                    <ul className="space-y-3">
                        {IMPORTANT_LINKS.map((l) => (
                            <li key={l.l}>
                                <Link
                                    to={l.to}
                                    data-testid={`footer-imp-${l.l.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'and')}`}
                                    className="text-sm text-white/75 hover:text-[var(--ebg-gold)] transition-colors"
                                >
                                    {l.l}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Locations */}
                <div className="col-span-2 md:col-span-2">
                    <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)] mb-5">Our Locations</div>
                    <ul className="space-y-4">
                        {LOCATIONS.map((loc) => (
                            <li key={loc.city} className="flex items-start gap-2.5">
                                <span className="w-6 h-6 rounded-full border border-[var(--ebg-gold-line)] flex items-center justify-center text-[var(--ebg-gold)] shrink-0 mt-0.5">
                                    <MapPin size={11} />
                                </span>
                                <div>
                                    <div className="text-sm text-white/90 font-serif">{loc.city}</div>
                                    <a
                                        href={CONTACT.tel}
                                        data-testid={`footer-phone-${loc.city.toLowerCase()}`}
                                        className="text-xs text-white/60 hover:text-[var(--ebg-gold)] transition-colors tabular-nums"
                                    >
                                        {loc.phone}
                                    </a>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Newsletter */}
                <div className="col-span-2 md:col-span-3">
                    <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)] mb-5">Newsletter</div>
                    <p className="text-sm text-white/70 leading-relaxed max-w-[300px]">
                        Subscribe to get the latest property updates and investment insights.
                    </p>
                    <form onSubmit={submit} data-testid="newsletter-form" className="mt-5 flex items-stretch border border-[var(--ebg-gold-line)]">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            data-testid="newsletter-email"
                            className="flex-1 min-w-0 bg-transparent outline-none placeholder:text-white/40 text-white px-4 py-3 text-sm"
                        />
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="newsletter-submit"
                            className="px-4 border-l border-[var(--ebg-gold-line)] text-[var(--ebg-gold)] hover:bg-[var(--ebg-gold)] hover:text-black transition-colors flex items-center justify-center disabled:opacity-60"
                            aria-label="Subscribe"
                        >
                            <ArrowRight size={16} />
                        </button>
                    </form>
                </div>
            </div>

            {/* Bottom bar */}
            <div className="border-t border-white/10">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/45">
                    <p>© {new Date().getFullYear()} EBG Realty. All Rights Reserved.</p>
                    <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2" data-testid="footer-policy-links">
                        {POLICY_LINKS.map((l) => (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    data-testid={`footer-policy-${l.to.slice(1)}`}
                                    className="hover:text-[var(--ebg-gold)] transition-colors whitespace-nowrap"
                                >
                                    {l.l}
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <div>
                        <span>
                            Design &amp; Development by{" "}
                            <a
                                href="https://www.reachvel.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-credit-reachvel"
                                className="text-[var(--ebg-gold)] hover:text-white font-bold transition-colors"
                            >
                                Reachvel
                            </a>
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
